import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { SHSidebar } from '@/sidebar/SHSidebar';
import { SHStudentViewSidebar } from '@/sidebar/SHStudentViewSidebar';
import { StudentSidebar } from '@/sidebar/StudentSidebar';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useMemberViewerRelation } from '~/hooks/useMemberViewerRelation';

export const Route = createFileRoute('/_app/_users/members/$memberId')({
  component: MemberProfileLayout,
});

/**
 * The routes the "you are viewing your student" notice may appear on: legacy
 * shows it on the profile component's pages only.
 *
 * `/profile/change-avatar` is left out because it 404s for the only viewer who
 * can see the notice, and `/profile/timeline` has no legacy counterpart under
 * the profile component. Scoped here rather than in each route, so it can't
 * render twice on nested routes.
 */
const PROFILE_COMPONENT_ROUTE_IDS: ReadonlySet<string> = new Set([
  '/_app/_users/members/$memberId/',
  '/_app/_users/members/$memberId/profile/',
  '/_app/_users/members/$memberId/profile/edit',
]);

function MemberProfileLayout() {
  const matches = useMatches();
  const { memberId } = Route.useParams();

  // /messages route screens follow this URL path, but don't share the same layout as the rest of the members/$memberId pages
  const isMessagesRoute = matches.some((m) =>
    (m.routeId as string).includes('/messages'),
  );

  if (isMessagesRoute) return <Outlet />;

  return (
    <MemberFrame
      memberId={memberId}
      isProfileComponentRoute={matches.some((m) =>
        PROFILE_COMPONENT_ROUTE_IDS.has(m.routeId as string),
      )}
    />
  );
}

/**
 * Split out so the relation queries below are never issued on the /messages
 * routes, which return above without a sidebar or a notice.
 */
function MemberFrame({
  memberId,
  isProfileComponentRoute,
}: {
  memberId: string;
  isProfileComponentRoute: boolean;
}) {
  const { t } = useTranslation();
  const { me, isStudent, isStudyHelper } = useCurrentUser();
  const { isSelf, isAssignedStudyHelper, memberProfileType } =
    useMemberViewerRelation(memberId);

  // TODO: coordinators can view students in their congregation group. For a
  // student who isn't their own, the only nav should be Profile, Timeline,
  // Course Study and Student Notes, with no Message button.

  // Shown when the displayed member isn't me, the viewer is the student's
  // assigned study helper (the query 404s for a non-student), the viewer's own
  // type is STUDY_HELPER, and the route is a profile page. The type check is
  // exact, since `useCurrentUser().isStudyHelper` also includes admins and the
  // follow-up roles.
  const showViewingStudentNotice =
    isAssignedStudyHelper &&
    !isSelf &&
    me?.profile_type === 'STUDY_HELPER' &&
    isProfileComponentRoute;

  // A student on their own page comes first: the supervisor check is also true
  // for a student viewing themselves, so tested first it would give every
  // student the supervisor sidebar.
  //
  // The second arm is the shell test "not me, and not a student", not
  // `useMemberViewerRelation().canSupervise`. That relation is narrower than
  // the sidebar's nav items, which each carry their own access check, so gating
  // on it would hide items from viewers the routes admit: follow-up roles would
  // lose Follow-ups and group coordinators Student Notes.
  //
  // A student viewing an unrelated member still gets `SHSidebar`; what they can
  // see is bounded by the profile read and each linked route's own gate.
  return (
    <Container
      style={showViewingStudentNotice ? { paddingTop: '20px' } : undefined}
    >
      {showViewingStudentNotice && (
        <AlertBox variant="warning">
          {t('profile.you-are-currently-viewing')}
        </AlertBox>
      )}
      <ContentWrapper>
        {isSelf && isStudent ? (
          <StudentSidebar />
        ) : isStudyHelper && !isSelf ? (
          <SHStudentViewSidebar
            userId={memberId}
            profileType={memberProfileType ?? undefined}
          />
        ) : (
          <SHSidebar userId={memberId} isSelf={isSelf} />
        )}

        <PageContent>
          <Outlet />
        </PageContent>
      </ContentWrapper>
    </Container>
  );
}

const Container = styled.div`
  background-color: var(--bg-default);
  display: flex;
  flex-direction: column;
  min-height: 70vh;
`;

const ContentWrapper = styled.div`
  display: flex;

  @media (max-width: 1099px) {
    flex-direction: column;
  }
`;

const PageContent = styled.div`
  box-sizing: border-box;
  flex: 1;
  max-width: calc(100% - 296px);
  overflow: visible;
  padding: 14px 0 50px 50px;

  @media (max-width: 1099px) {
    max-width: 100%;
    padding: 14px 16px 50px;
  }
`;
